import { useEffect, useState } from "react";
import PageTitle from "../../../components/common/PageTitle";
import { useUI } from "../../../context/ui";
import { useLocation, useNavigate } from "react-router-dom";
import FullPageLoader from "../../../components/ui/FullPageLoader";
import RSModal from "../../../components/ui/RSModal";
import CustomerDetails from "./CustomerDetails";
import ButtonTertiary from "../../../components/ui/ButtonTertiary";
import { API_URL } from "../../../utilities/constants";
import useQueryParams from "../../../hooks/useQueryParams";
import Pagination from "../../../components/common/Pagination";
import ButtonTextIcon from "../../../components/ui/ButtonTextIcon";
import { RiArrowGoBackFill } from "react-icons/ri";
import ResultsPerPage from "../../_common/ResultsPerPage";

const buildQueryParams = (params) =>
  Object.entries(params)
    .filter(([_, value]) => value !== "")
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

const CustomerPage = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { setSegmentTitle } = useUI();
  const [customers, setCustomers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [wallets, setWallets] = useState([]);
  const { updateQueryParam, queryParams } = useQueryParams();
  const pageNo = parseInt(queryParams.get("pageNo")) || 0;
  const pageSize = parseInt(queryParams.get("pageSize")) || 10;
  const navigate = useNavigate();
  useEffect(() => {
    setSegmentTitle("Customers");
    fetchRewardsData();
    fetchCustomersData();
  }, [location.search, setSegmentTitle]);

  const fetchRewardsData = async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        `${API_URL}/gasolina/api/1.0.0/octorewards/program`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );
      if (resp.ok) {
        const response = await resp.json();
        if (response.data.length > 0) {
          setTiers(response.data[0].tiers);
          setWallets(response.data[0].rewardCurrencies);
        }
      }
    } catch (e) {
      console.error("Error fetching rewards data", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomersData = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams({
        pageNo,
        pageSize,
      });
      const resp = await fetch(
        `${API_URL}/gasolina/api/1.0.0/octorewards/customers?sortOrder=DESC&${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );
      if (resp.ok) {
        const response = await resp.json();
        setCustomers(response.data);
      }
    } catch (e) {
      console.error("Error fetching customers data", e);
    } finally {
      setLoading(false);
    }
  };

  const getBackgroundGradient = (minPointsRequired, tiers) => {
    const sortedTiers = tiers.sort(
      (a, b) => b.minPointsRequired - a.minPointsRequired
    );
    const rank = sortedTiers.findIndex(
      (tier) => tier.minPointsRequired === minPointsRequired
    );
    if (rank === 1) {
      return "linear-gradient(135deg, #d4af37, #f5e6a3, #ffd700, #fffacd)";
    } else if (rank === 2) {
      return "linear-gradient(135deg, rgb(103, 102, 102), #f2f2f2, rgb(167, 167, 167), #d9d9d9)";
    } else if (rank === 0) {
      return "linear-gradient(135deg, #1e5799, #2989d8, #207cca, #7db9e8)";
    } else {
      return "linear-gradient(135deg, #d3d3d3, #e0e0e0, #cfcfcf, #f5f5f5)";
    }
  };

  return (
    <section>
      <PageTitle title="Customers" />
      <FullPageLoader isActive={loading} />
      <div className="flex center-left responsive-container">
        <div className="flex center-left" style={{ gap: "10px" }}>
          <ButtonTextIcon
            type="button"
            title="Go Back"
            onClick={() => navigate("/rewards")}
            icon={<RiArrowGoBackFill />}
            className="responsive-button"
          />
        </div>
        <div className="responsive-button">
          <ResultsPerPage
            targetURL={`${location.pathname}?pageNo=0&pageSize=`}
          />
        </div>
      </div>
      <div className="rs-table-holder">
        <table
          style={{
            borderCollapse: "collapse",
          }}
          className="rs-table"
        >
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone No</th>
              <th className="flex center-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {customers?.content?.length > 0 ? (
              customers?.content?.map((user, i) => (
                <tr
                  key={user.externalCustomerId}
                  style={{
                    textAlign: "center",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  <td>{pageNo * pageSize + i + 1}</td>
                  <td>{user?.fullName || "-"}</td>
                  <td>{user.email}</td>
                  <td>{user.phone || "-"}</td>
                  <td>
                    <div className="flex center-center" style={{ gap: "12px" }}>
                      <ButtonTertiary
                        title="View"
                        actionType="view"
                        onClick={() => {
                          updateQueryParam(
                            "customerId",
                            user?.externalCustomerId
                          );
                          navigate(
                            "/rewards/customer/" + user?.externalCustomerId
                          );
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {customers && (
        <Pagination
          totalRecords={customers?.totalElements}
          pageNo={pageNo}
          pageSize={pageSize}
          onPageChange={(newPage) => updateQueryParam("pageNo", newPage)}
        />
      )}
    </section>
  );
};

export default CustomerPage;
